import { type DocMeta, newDocPrefixes } from "@klarity/schema";
import { Field, TextInput } from "../../components/ui/Field";
import { Select } from "../../components/ui/Select";
import type { DocKind } from "../../lib/api/models";
import type { MetaErrors } from "../../lib/meta";

type Props = {
  kind: DocKind;
  meta: DocMeta;
  onChange: (meta: DocMeta) => void;
  errors?: MetaErrors;
  readOnly?: boolean;
};

export function MetaForm({ kind, meta, onChange, errors = {}, readOnly = false }: Props) {
  if (kind === "page") {
    return <p className="text-sm text-ink-2">Wiki pages have no document details and export without a cover.</p>;
  }
  const set = <K extends keyof DocMeta>(key: K, value: DocMeta[K]) => onChange({ ...meta, [key]: value });
  const text = (
    key: "code" | "subtitle" | "docType" | "client" | "version",
    label: string,
    hint?: string,
    mono = false,
  ) => (
    <Field label={label} hint={hint} error={errors[key]}>
      {(id, describedBy) => (
        <TextInput
          id={id}
          aria-describedby={describedBy}
          invalid={Boolean(errors[key])}
          readOnly={readOnly}
          className={mono ? "font-mono" : undefined}
          value={meta[key] ?? ""}
          onChange={(e) => set(key, e.target.value)}
        />
      )}
    </Field>
  );
  return (
    <div className="grid gap-4">
      {text("code", "Code", `Starts with ${newDocPrefixes("-")}. Used in links, the API and file names.`, true)}
      {text("subtitle", "Subtitle")}
      {text("docType", "Document type", "The grey line on the cover, for example Runbook or Baseline Documentation.")}
      <div className="grid grid-cols-2 items-start gap-3">
        <Field label="Audience">
          {(id) => (
            <Select
              id={id}
              disabled={readOnly}
              value={meta.audience ?? "internal"}
              onChange={(v) => set("audience", v as "internal" | "client")}
              options={[
                { value: "internal", label: "Internal" },
                { value: "client", label: "Client" },
              ]}
            />
          )}
        </Field>
        <Field label="Language">
          {(id) => (
            <Select
              id={id}
              disabled={readOnly}
              value={meta.lang ?? ""}
              onChange={(v) => set("lang", (v || undefined) as "en" | "nl" | undefined)}
              options={[
                { value: "", label: `Default (${meta.audience === "client" ? "Dutch" : "English"})` },
                { value: "en", label: "English" },
                { value: "nl", label: "Dutch" },
              ]}
            />
          )}
        </Field>
      </div>
      {meta.audience === "client" ? text("client", "Client") : null}
      <div className="grid grid-cols-2 items-start gap-3">
        <Field label="Date" hint="YYYY-MM or YYYY-MM-DD. Empty means the current month at export." error={errors.date}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              invalid={Boolean(errors.date)}
              readOnly={readOnly}
              placeholder="2026-10"
              value={meta.date ?? ""}
              onChange={(e) => set("date", e.target.value)}
            />
          )}
        </Field>
        {text("version", "Version")}
      </div>
      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          disabled={readOnly}
          checked={meta.toc === true}
          onChange={(e) => set("toc", e.target.checked)}
        />
        Table of contents
      </label>
      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          disabled={readOnly}
          checked={meta.numbered !== false}
          onChange={(e) => set("numbered", e.target.checked)}
        />
        Number headings (1., 1.1)
      </label>
    </div>
  );
}
